import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import AvItemWithLogoGroup, { IconSize } from '@components/AvItemWithLogoGroup';
import ColorDescription from '@components/ColorPicker/ColorDescription';
import { IconVariant } from '@components/ItemWithLogo';
import { AvTooltip, ColorItem } from '@fabric-ui/components';
import { More } from '@fabric-ui/icons';
import { dotStyle, flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Grow, IconButton, Paper, Popper, Typography, useTheme } from '@mui/material';
import { FormattingRule } from '../views/FormattingRules/types';

const moreIcon = <More style={{ ...iconSize(16), transform: `rotate(90deg)` }} />;

const boxStyle = (theme, isHorizontal, isOutlined) => ({
  ...flex.wrap,
  flexDirection: isHorizontal ? 'row' : 'column',
  columnGap: isOutlined ? 1 : 1.75,
  rowGap: isHorizontal ? '2px' : 2,
  color: theme.palette.colors.neutrals[500],
  [theme.breakpoints.down('laptop')]: {
    flexDirection: 'row',
  },
  h5: {
    color: theme.palette.colors.neutrals[800],
    paddingLeft: 3,
  },
});

const outlinedStyle = {
  px: 2,
  py: '2px',
  border: theme => `1px solid ${theme.palette.colors.neutrals[400]}`,
  color: theme => theme.palette.colors.neutrals[800],
  fontWeight: 600,
  borderRadius: '8px',
  lineHeight: 1.5,
};

interface AvLegendProps {
  series: ISeries[];
  nameKey?: string;
  isHorizontal?: boolean;
  isOutlined?: boolean;
  getMaxLegendsToShow?: (calculatedMaxLegends: number) => number;
}

export interface ISeries {
  name: string;
  component?: ReactElement<any> | string;
  color?: string | string[];
  formattingRule?: FormattingRule;
  value?: number;
  shape?: ReactElement<any>;
}

function calculateMaxLegendsToShow(mainDivWidth, legendTexts, paddingPerItem = 5) {
  const maxRowsWithLegends = 2;
  const paddingOnLastRowForEllipsis = 80;
  const legendWidths = legendTexts.map(text => getTextWidth(text));
  const { index } = legendWidths.reduce(
    (acc, width, currentIndex) => {
      const sumWidth = acc.sumWidth + width + paddingPerItem;
      return {
        index: sumWidth <= maxRowsWithLegends * mainDivWidth - paddingOnLastRowForEllipsis ? currentIndex + 1 : acc.index,
        sumWidth,
      };
    },
    { index: 0, sumWidth: 0 }
  );

  return index + 1;
}

function getTextWidth(text) {
  const widthOfDotIncludingPadding = 26;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  if (!context) {
    return 40;
  }
  context.font = '12px Inter';
  return context.measureText(text).width + widthOfDotIncludingPadding;
}

const AvLegend: React.FC<AvLegendProps> = ({ series, nameKey = 'name', isHorizontal = false, isOutlined = false, getMaxLegendsToShow }) => {
  const mainDivRef = useRef(null);
  const [mainDivWidth, setMainDivWidth] = useState(100);
  const [anchorEl, setAnchorEl] = useState<Element>();

  const disableEllipsis = isOutlined || !isHorizontal;
  useEffect(() => {
    if (disableEllipsis) {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      setMainDivWidth(width);
    });

    if (mainDivRef.current) {
      observer.observe(mainDivRef.current);
    }

    return () => {
      if (mainDivRef.current) {
        observer.unobserve(mainDivRef.current);
      }
    };
  }, []);

  const handleHoverEllipses = ({ currentTarget }) => setAnchorEl(anchorEl ? undefined : currentTarget);

  const maxLegendsToShow = useMemo(() => {
    const legendTexts = series.map(item => {
      if (nameKey in item && typeof item[nameKey] === 'string') {
        return item[nameKey];
      }
      return '';
    });
    if (disableEllipsis) {
      return series.length;
    }
    const calculatedMaxLegends = calculateMaxLegendsToShow(mainDivWidth, legendTexts);
    if (getMaxLegendsToShow) {
      return getMaxLegendsToShow(calculatedMaxLegends);
    }
    return calculatedMaxLegends;
  }, [disableEllipsis, series, mainDivWidth, getMaxLegendsToShow]);

  const visibleLegends = series.slice(0, maxLegendsToShow);
  const hiddenLegends = series.slice(maxLegendsToShow);
  const hasHiddenLegends = Boolean(hiddenLegends.length);
  const theme = useTheme();
  return (
    <Box sx={theme => boxStyle(theme, isHorizontal, isOutlined)} ref={mainDivRef} className={hasHiddenLegends ? 'hidden-legends' : ''}>
      {visibleLegends.map(item => {
        if (!item[nameKey]) {
          return null;
        }
        const { color, value, component, shape, formattingRule } = item;
        return (
          <div key={item[nameKey]}>
            <Box sx={{ ...flex.itemsCenter, gap: '6px', ...(isOutlined ? outlinedStyle : {}) }}>
              {color &&
                (Array.isArray(color) ? (
                  <AvTooltip
                    variant="message"
                    color="white"
                    hasWidthLimit={false}
                    title={formattingRule && <ColorDescription rule={formattingRule} colorSize={16} />}>
                    <div>
                      <AvItemWithLogoGroup
                        items={[...new Set<string>(color)]}
                        variant={IconVariant.color}
                        CustomItemRender={ColorItem}
                        size={IconSize.collapsedXXSmall}
                        limit={3}
                      />
                    </div>
                  </AvTooltip>
                ) : (
                  shape || <div style={{ ...dotStyle(color), flexShrink: 0 }} />
                ))}
              <Box
                sx={theme => ({
                  color: theme.palette.colors.neutrals[isOutlined ? 800 : 550],
                  fontSize: 12,
                })}>
                {component || item[nameKey]}
              </Box>
            </Box>
            {value ? <Typography variant="h5">{value}</Typography> : null}
          </div>
        );
      })}
      {Boolean(hiddenLegends.length) && (
        <Box onMouseEnter={handleHoverEllipses} onMouseLeave={handleHoverEllipses}>
          {/* @ts-ignore */}
          <IconButton active={Boolean(anchorEl)} size="small" sx={{ pb: 2, color: theme.palette.colors.neutrals[550] }}>
            {moreIcon}
          </IconButton>
          <Popper
            open={Boolean(anchorEl)}
            placement="bottom-start"
            anchorEl={anchorEl}
            transition
            sx={{
              zIndex: ({ zIndex }) => zIndex.modal,
              boxShadow: '0px 6px 20px 0px #00000033',
            }}>
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                <Paper
                  sx={{
                    ...flex.col,
                    width: 275,
                    boxShadow: 'none',
                    maxHeight: '300px',
                    overflow: 'auto',
                    py: 1,
                  }}>
                  {hiddenLegends.map(item => {
                    const { color, value, component, shape } = item;
                    return !item[nameKey] ? null : (
                      <Box
                        key={item[nameKey]}
                        sx={{
                          ...flex.row,
                          alignItems: 'center',
                          mx: 1,
                          py: 0.5,
                          ':hover': { backgroundColor: theme.palette.colors.neutrals[200] },
                        }}>
                        <Box sx={{ ...flex.itemsCenter, gap: 1.5, minHeight: '24px' }}>
                          {color &&
                            (shape || (
                              <div
                                style={{
                                  ...dotStyle(color),
                                  flexShrink: 0,
                                  marginLeft: '8px',
                                }}
                              />
                            ))}
                          <Box
                            sx={theme => ({
                              color: theme.palette.colors.neutrals[800],
                              fontWeight: 400,
                              fontSize: 12,
                            })}>
                            {component || item[nameKey]}
                          </Box>
                        </Box>
                        {value ? <Typography variant="h5">{value}</Typography> : null}
                      </Box>
                    );
                  })}
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      )}
    </Box>
  );
};

export default AvLegend;
